import { useId, useState } from 'react';
import clsx from 'clsx';
import Slider, { type CustomArrowProps } from 'react-slick';
import { CaretLeft, CaretRight } from '@hungryroot/icons';
import { getImageProps } from '@hungryroot/image';
import { IconButton } from '@hungryroot/ramen';

import styles from './ImageCarousel.module.css';

const sliderConfiguration = Object.freeze({
  dots: false,
  arrows: true,
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  initialSlide: 0,
  swipeToSlide: true,
  lazyLoad: 'progressive',
  speed: 150,
  accessibility: true,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        dots: true,
        arrows: false,
      },
    },
  ],
  nextArrow: <NextArrow />,
  prevArrow: <PrevArrow />,
});

type ImageCarouselProps = {
  images: {
    src: string;
    alt: string;
  }[];
};

export function ImageCarousel(props: ImageCarouselProps) {
  const { images } = props;

  const [currentSlide, setCurrentSlide] = useState<number>(
    sliderConfiguration.initialSlide
  );

  const dotIdInstancePrefix = useId();

  if (images.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Slider
        {...sliderConfiguration}
        beforeChange={(_, index) => {
          setCurrentSlide(index);
          const dot = document.getElementById(`${dotIdInstancePrefix}${index}`);

          // This is a hack to ensure any CSS changes to the dot are applied before
          // scrolling to prevent the scrolling from using calculations from old
          // dot sizes.
          setTimeout(() => {
            dot?.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center',
            });
          }, 10);
        }}
        appendDots={(dots) => {
          return (
            <div>
              <div
                className={styles['dots-container']}
                data-greater-than-5-dots={images.length > 5}
                data-testid="dots-container"
              >
                {dots}
              </div>
            </div>
          );
        }}
        customPaging={(index: number) => (
          <button
            data-dot-size={getDotSize({
              currentSlide,
              index,
              totalSlides: images.length,
            })}
            type="button"
            aria-label={`Go to slide ${index + 1}`}
            id={`${dotIdInstancePrefix}${index}`}
          />
        )}
      >
        {images.map((image) => {
          const { src, alt } = image;

          return (
            <div key={src}>
              <img
                className={styles.image}
                {...getImageProps({ src: src, width: 600 })}
                alt={alt}
              />
            </div>
          );
        })}
      </Slider>
    </div>
  );
}

function NextArrow(props: CustomArrowProps) {
  const { onClick, currentSlide, slideCount } = props;

  if (!slideCount) {
    return null;
  }

  if (currentSlide === slideCount - 1) {
    return null;
  }

  return (
    <IconButton
      className={clsx(styles.caret, styles['caret-right'])}
      icon={<CaretRight />}
      style="muted"
      prominence="secondary"
      size="small"
      ariaLabel="Next slide"
      onClick={onClick}
    />
  );
}

function PrevArrow(props: CustomArrowProps) {
  const { onClick, currentSlide } = props;

  if (currentSlide === 0) {
    return null;
  }

  return (
    <IconButton
      className={clsx(styles.caret, styles['caret-left'])}
      icon={<CaretLeft />}
      style="muted"
      prominence="secondary"
      size="small"
      ariaLabel="Previous slide"
      onClick={onClick}
    />
  );
}

function getDotSize({
  currentSlide,
  index,
  totalSlides,
}: {
  currentSlide: number;
  index: number;
  totalSlides: number;
}) {
  if (totalSlides <= 3) {
    return 'large';
  }

  if (currentSlide === index) {
    return 'large';
  }

  if (currentSlide - 1 === index || currentSlide + 1 === index) {
    return 'medium';
  }

  if (currentSlide - 2 === index || currentSlide + 2 === index) {
    return 'small';
  }

  return 'small';
}
